"use client";
import { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import { Menu, X, ChevronDown } from 'lucide-react';
import Link from 'next/link';
import Image from 'next/image';
import { companyLinks, clientSegmentsLinks, capabilitiesLinks } from "@/lib/business";

const Header = () => {
    const [scrolled, setScrolled] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [mobileCapabilitiesOpen, setMobileCapabilitiesOpen] = useState(false);
    const [mobileClientSegmentsOpen, setMobileClientSegmentsOpen] = useState(false);
    const [capabilitiesDropdownOpen, setCapabilitiesDropdownOpen] = useState(false);
    const [clientSegmentsDropdownOpen, setClientSegmentsDropdownOpen] = useState(false);

    const capabilitiesDropdownRef = useRef(null);
    const clientSegmentsDropdownRef = useRef(null);

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 10);
        const handleClickOutside = (event) => {
            if (capabilitiesDropdownRef.current && !capabilitiesDropdownRef.current.contains(event.target)) {
                setCapabilitiesDropdownOpen(false);
            }
            if (clientSegmentsDropdownRef.current && !clientSegmentsDropdownRef.current.contains(event.target)) {
                setClientSegmentsDropdownOpen(false);
            }
        };

        window.addEventListener('scroll', handleScroll);
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            window.removeEventListener('scroll', handleScroll);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return (
        <header className={`fixed left-0 right-0 z-[100] transition-all duration-300 ${scrolled
            ? 'top-0 bg-white shadow-md'
            : 'top-0 md:top-10 bg-white'
            }`}>
            <div className="max-w-7xl mx-auto px-6">
                <div className="flex items-center justify-between h-20">

                    {/* Logo */}
                    <Link href="/" className="flex items-center gap-2 flex-shrink-0">
                        <div className="">
                            <Image
                            src="/images/logo.png"
                            alt="Seternity Solutions"
                            width={120}
                            height={40}
                            className="h-7 w-auto"
                            priority
                        />
                        </div>
                        <h2 className="text-xl md:text-2xl text-[#1F95DB] font-[450] [font-family:Arial,sans-serif] tracking-wide">
                            Seternity Solutions
                        </h2>
                    </Link>

                    {/* Desktop Navigation */}
                    <nav className="hidden lg:flex items-center space-x-8">
                        {companyLinks.map((item) => (
                            item.label === "Services" ? (
                                <div key={item.id} className="relative" ref={capabilitiesDropdownRef} onMouseEnter={() => setCapabilitiesDropdownOpen(true)} onMouseLeave={() => setCapabilitiesDropdownOpen(false)}>
                                    <button
                                        type="button"
                                        aria-expanded={capabilitiesDropdownOpen}
                                        aria-haspopup="menu"
                                        className="text-gray-700 hover:text-blue-600 px-3 py-2 text-sm font-medium flex items-center gap-1"
                                    >
                                        {item.label}
                                        <ChevronDown className={`w-4 h-4 transition-transform ${capabilitiesDropdownOpen ? 'rotate-180' : ''}`} />
                                    </button>

                                    {/* Capabilities Dropdown */}
                                    {capabilitiesDropdownOpen && (
                                        <div className="
        absolute top-full left-0 mt-0 
        w-[400px]               /* FIXED LARGE WIDTH */
        bg-white border border-gray-100 
        rounded-2xl             /* SOFTER CORNERS */
        shadow-[0_20px_50px_rgba(0,0,0,0.15)] /* DEEPER SHADOW */
        p-4                     /* MORE INNER PADDING */
        animate-in fade-in slide-in-from-top-3 duration-200
    ">
                                            <div className="grid grid-cols-1 gap-1"> {/* Grid for better structure */}
                                                {capabilitiesLinks.map((link, index) => (
                                                    <Link
                                                        key={index}
                                                        href={link.href}
                                                        className="
                        group flex items-center justify-between
                        px-5 py-4       /* LARGE CLICKABLE AREA */
                        rounded-xl      /* INNER ITEMS ROUNDED */
                        hover:bg-slate-50 
                        transition-all duration-200
                    "
                                                        onClick={() => setCapabilitiesDropdownOpen(false)}
                                                    >
                                                        <span className="text-[15px] font-medium text-gray-700 group-hover:text-blue-600 transition-colors">
                                                            {link.label}
                                                        </span>
                                                        {/* Optional: subtle arrow on hover to fill space */}
                                                        <div className="opacity-0 group-hover:opacity-100 transition-opacity transform translate-x-[-10px] group-hover:translate-x-0">
                                                            <ChevronDown className="-rotate-90 w-4 h-4 text-blue-400" />
                                                        </div>
                                                    </Link>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : item.label === "Client Segments" ? (
                                <div key={item.id} className="relative" ref={clientSegmentsDropdownRef} onMouseEnter={() => setClientSegmentsDropdownOpen(true)} onMouseLeave={() => setClientSegmentsDropdownOpen(false)}>
                                    <button
                                        type="button"
                                        aria-expanded={clientSegmentsDropdownOpen}
                                        aria-haspopup="menu"
                                        className="text-gray-700 hover:text-blue-600 px-3 py-2 text-sm font-medium flex items-center gap-1"
                                    >
                                        {item.label}
                                        <ChevronDown className={`w-4 h-4 transition-transform ${clientSegmentsDropdownOpen ? 'rotate-180' : ''}`} />
                                    </button>

                                    {clientSegmentsDropdownOpen && (
                                        <div className="
        absolute top-full left-0 mt-0 
        w-[400px]                       /* MATCHED LARGE WIDTH */
        bg-white border border-gray-100 
        rounded-2xl                     /* MATCHED ROUNDED CORNERS */
        shadow-[0_20px_50px_rgba(0,0,0,0.15)] 
        p-4                             /* MATCHED INNER PADDING */
        animate-in fade-in slide-in-from-top-3 duration-200
    ">
                                            <div className="grid grid-cols-1 gap-1">
                                                {clientSegmentsLinks.map((link, index) => (
                                                    <Link
                                                        key={index}
                                                        href={link.href}
                                                        className="
                        group flex items-center justify-between
                        px-5 py-4               /* LARGE CLICKABLE AREA */
                        rounded-xl              /* MATCHED INNER ROUNDING */
                        hover:bg-slate-50 
                        transition-all duration-200
                    "
                                                        onClick={() => setClientSegmentsDropdownOpen(false)}
                                                    >
                                                        <span className="text-[15px] font-medium text-gray-700 group-hover:text-blue-600 transition-colors">
                                                            {link.label}
                                                        </span>

                                                        {/* Visual cue to fill the larger horizontal space */}
                                                        <div className="opacity-0 group-hover:opacity-100 transition-all transform translate-x-[-10px] group-hover:translate-x-0">
                                                            <ChevronDown className="-rotate-90 w-4 h-4 text-blue-400" />
                                                        </div>
                                                    </Link>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <Link
                                    key={item.id}
                                    href={item.href}
                                    className="text-gray-700 hover:text-blue-600 px-3 py-2 text-sm font-medium"
                                >
                                    {item.label}
                                </Link>
                            )
                        ))}
                    </nav>

                    {/* Desktop CTA */}
                    <div className="hidden lg:block">
                        <Link href="/contact">
                            <Button className="bg-brand cursor-pointer text-white font-medium px-6 py-2 rounded-full shadow-lg">
                               Contact Us
                            </Button>
                        </Link>
                    </div>

                    {/* Mobile Toggle */}
                    <div className="lg:hidden flex items-center">
                        <button
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
                            aria-expanded={mobileMenuOpen}
                            className="p-2 rounded-md text-gray-700 hover:bg-gray-100"
                        >
                            {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile Menu Dropdown */}
            {mobileMenuOpen && (
                <div className="lg:hidden absolute top-full left-0 w-full bg-white border-t shadow-2xl overflow-y-auto max-h-[calc(100vh-20px)]">
                    <div className="px-6 py-8 space-y-4">
                        {companyLinks.map((item) => (
                            <div key={`mobile-${item.id}`}>
                                {item.label === "Services" ? (
                                    <div className="flex flex-col">
                                        <button
                                            onClick={() => setMobileCapabilitiesOpen(!mobileCapabilitiesOpen)}
                                            className="flex items-center justify-between w-full py-3 text-lg font-semibold text-gray-800"
                                        >
                                            {item.label}
                                            <ChevronDown className={`w-5 h-5 transition-transform ${mobileCapabilitiesOpen ? 'rotate-180' : ''}`} />
                                        </button>

                                        {mobileCapabilitiesOpen && (
                                            <div className="pl-4 mt-2 space-y-2 border-l-2 border-blue-100">
                                                {capabilitiesLinks.map((link, index) => (
                                                    <Link
                                                        key={index}
                                                        href={link.href}
                                                        className="block py-2 text-base text-gray-600 active:text-blue-600"
                                                        onClick={() => setMobileMenuOpen(false)}
                                                    >
                                                        {link.label}
                                                    </Link>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ) : item.label === "Client Segments" ? (
                                    <div className="flex flex-col">
                                        <button
                                            onClick={() => setMobileClientSegmentsOpen(!mobileClientSegmentsOpen)}
                                            className="flex items-center justify-between w-full py-3 text-lg font-semibold text-gray-800"
                                        >
                                            {item.label}
                                            <ChevronDown className={`w-5 h-5 transition-transform ${mobileClientSegmentsOpen ? 'rotate-180' : ''}`} />
                                        </button>

                                        {mobileClientSegmentsOpen && (
                                            <div className="pl-4 mt-2 space-y-2 border-l-2 border-blue-100">
                                                {clientSegmentsLinks.map((link, index) => (
                                                    <Link
                                                        key={index}
                                                        href={link.href}
                                                        className="block py-2 text-base text-gray-600 active:text-blue-600"
                                                        onClick={() => setMobileMenuOpen(false)}
                                                    >
                                                        {link.label}
                                                    </Link>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <Link
                                        href={item.href}
                                        className="block py-3 text-lg font-semibold text-gray-800"
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        {item.label}
                                    </Link>
                                )}
                            </div>
                        ))}

                        <div className="pt-6">
                            <Button className="bg-brand w-full text-white font-medium py-6 text-lg rounded-xl shadow-lg">
                                Get Started
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
};

export default Header;
